import { useMemo } from 'react';
import { Stack, Step, StepLabel, Stepper } from '@mui/material';

import { getCaseAssetsApi } from 'api/cases/query';
import SkeletonContainer from 'components/v2/container/SkeletonContainer';
import Modal from 'components/v2/modal/Modal';
import { MODALS_STORE, TASKS_MODAL_ID } from 'constants/ui/modal';
import { MODAL_WIDTH } from 'constants/ui/style';
import { useLocalization } from 'localization';
import virtualStagingModalContent from './VirtualStagingModalContent';
import TaskModalTitle from 'components/v2/shared/TaskModalTitle';
import { virtualStagingMutation } from 'api/tasks/query';
import { getOrderLinesApi } from 'api/orders/query';
import { TaskStatus } from 'api';

const VirtualStagingModal = () => {
  const { t } = useLocalization();
  const { toggleModal, isModalOpen, getModalData, setModalData } = MODALS_STORE.tasks();

  const caseId = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.task?.caseId;
  const taskId = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.task?.id ?? '';
  const materialId = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.materialIds ?? [];
  const reference = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.task?.caseReference;
  const roomType = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.roomType ?? '';
  const interiorCode = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.code ?? '';
  const label = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.label ?? '';
  const furniture = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.furniture ?? '';
  const orderId = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.task?.orderId ?? '';
  const orderLineId = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.task?.orderlineId ?? '';
  const activeStep = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.activeStep ?? 0;
  const taskStatus = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.task?.status;

  const { isLoading } = getCaseAssetsApi(caseId);
  const { mutateAsync, isLoading: isMutateLoading } = virtualStagingMutation(taskId);
  const { data } = getOrderLinesApi(orderId);

  const steps = virtualStagingModalContent;
  const { content } = steps[activeStep];

  const handleClickConfirm = async () => {
    if (activeStep === steps.length - 1) {
      const batchId = data?.find(item => item.id === orderLineId)?.batchId ?? '';
      await mutateAsync({
        materialIds: materialId.map(String),
        batchId,
        code: interiorCode,
        label,
        roomType,
        furniture,
      });
      toggleModal(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE);
    } else {
      setModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE, {
        ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE),
        virtualStaging: {
          ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE).virtualStaging,
          activeStep: activeStep + 1,
        },
      });
    }
  };

  const handleClickCancel = () => {
    if (activeStep === 0) {
      toggleModal(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE);
    } else {
      setModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE, {
        ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE),
        virtualStaging: {
          ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE).virtualStaging,
          activeStep: activeStep - 1,
        },
      });
    }
  };

  const isDisabled = useMemo(() => {
    switch (activeStep) {
      case 0:
        return materialId.length > 0;
      case 1:
        return !!roomType;
      case 2:
        return !!interiorCode;
      case 3:
      case 4:
        return true;
      default:
        break;
    }
  }, [activeStep, materialId, roomType, interiorCode]);

  return (
    <Modal
      isOpen={isModalOpen(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)}
      toggleModal={() => toggleModal(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)}
      modalTitle={<TaskModalTitle title={t('modals.virtualStaging.title')} caseId={reference ?? ''} />}
      width={MODAL_WIDTH.xxxl}
      onConfirm={handleClickConfirm}
      onCancel={handleClickCancel}
      confirmBtnLabel={activeStep === 4 ? t('common.actions.confirm') : t('navigation.next')}
      cancelBtnLabel={activeStep !== 0 ? t('navigation.back') : t('common.actions.cancel')}
      disableConfirm={!isDisabled}
      isLoading={isMutateLoading}
      hideFooter={taskStatus === TaskStatus.Complete}
    >
      <SkeletonContainer columns={{ xs: 1 }} rows={{ xs: 1 }} loading={isLoading}>
        <Stack gap="1.25rem">
          <Stepper activeStep={activeStep}>
            {steps.map(({ titleKey }) => {
              const stepProps: { completed?: boolean } = {};
              return (
                <Step key={titleKey} {...stepProps}>
                  <StepLabel>{t(titleKey)}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          {content}
        </Stack>
      </SkeletonContainer>
    </Modal>
  );
};

export default VirtualStagingModal;
